import { useMemo } from "react";
import client from "@/lib/api";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { useNavigate } from "@/lib/useNav";
import { TaskCard } from "@/components/shared/TaskCard";
import { HealthPill } from "@/components/shared/Pills";
import { sortTasksDefault, dueKey } from "@/lib/taskSort";
import { PRIORITIES } from "@/lib/constants";
import { TASK_BUCKETS } from "@/lib/dashboardBlocks";
import { TypeIcon } from "@/lib/activityTypes";
import { formatDateShort } from "@/lib/format";
import { useDash } from "@/components/dashboard/DashContext";
import { useBlockData, BlockEmpty } from "@/components/dashboard/useBlockData";

const iso = (d) => d.toLocaleDateString("en-CA");
function bucketTests() {
  const now = new Date();
  const today = iso(now);
  const sun = new Date(now); sun.setDate(now.getDate() + (6 - ((now.getDay() + 6) % 7)));
  const n7 = new Date(now); n7.setDate(now.getDate() + 7);
  const week = iso(sun), next7 = iso(n7);
  return {
    due_by_today: (d) => d && d <= today,
    overdue: (d) => d && d < today,
    today: (d) => d === today,
    this_week: (d) => d && d > today && d <= week,
    next_7: (d) => d && d > today && d <= next7,
    upcoming: (d) => !d || d > today,
    no_date: (d) => !d,
    all: () => true,
  };
}

const prio = (t) => { const i = PRIORITIES.indexOf(t.priority); return i < 0 ? 2.5 : i; };
function sortTasks(list, sort) {
  if (sort === "priority") return [...list].sort((a, b) => prio(a) - prio(b) || dueKey(a).localeCompare(dueKey(b)));
  if (sort === "due") return [...list].sort((a, b) => dueKey(a).localeCompare(dueKey(b)));
  return sortTasksDefault(list);
}

const COL_TINT = { due_by_today: "text-amber-600", overdue: "text-red-600", today: "text-amber-600" };

export function TodoBlock({ block }) {
  const { data, cardProps } = useDash();
  const s = block.settings || {};
  const tasks = useMemo(() => {
    if (!data) return [];
    const mine = data.my_tasks || [], linked = data.delegated_tasks || [];
    if (s.source === "linked") return linked;
    if (s.source === "both") { const ids = new Set(mine.map((t) => t.id)); return [...mine, ...linked.filter((t) => !ids.has(t.id))]; }
    return mine;
  }, [data, s.source]);
  // Only the next open occurrence of each recurring series
  const deduped = useMemo(() => {
    const first = {};
    tasks.forEach((t) => {
      if (!t.series_id) return;
      const cur = first[t.series_id];
      if (!cur || (t.due_date || "9999") < (cur.due_date || "9999")) first[t.series_id] = t;
    });
    return tasks.filter((t) => !t.series_id || first[t.series_id].id === t.id);
  }, [tasks]);
  const tests = bucketTests();
  const cols = (s.columns?.length ? s.columns : ["all"]).filter((c) => tests[c]);
  return (
    <div className="grid gap-x-5 gap-y-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
      {cols.map((c) => {
        const list = sortTasks(deduped.filter((t) => tests[c](t.due_date)), s.sort);
        return (
          <div key={c} data-testid={`block-${block.id}-col-${c}`}>
            <div className="mb-2 flex items-center gap-2 border-b border-slate-100 pb-1.5">
              <span className={cn("text-[11px] font-bold uppercase tracking-wider", COL_TINT[c] || "text-slate-400")}>{TASK_BUCKETS[c]}</span>
              <span className="rounded-full bg-slate-100 px-1.5 text-[10px] font-semibold text-slate-500">{list.length}</span>
            </div>
            {list.length === 0 ? <BlockEmpty>Nothing here</BlockEmpty> : (
              <div className="space-y-2">
                {list.slice(0, s.limit || 8).map((t) => <TaskCard key={t.id} task={t} draggable={false} {...cardProps} />)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function ActivitiesBlock({ block }) {
  const { user } = useAuth();
  const { openTask } = useUI();
  const s = block.settings || {};
  const [rows] = useBlockData(() => client.get("/actions", { params: { view: "completed", assigned_to_id: s.scope === "team" ? undefined : user.id } }).then((r) => r.data), [s.scope, user.id]);
  if (!rows) return null;
  const list = rows
    .filter((a) => !s.types?.length || s.types.includes(a.activity_type || a.action_type))
    .sort((a, b) => (b.completed_date || "").localeCompare(a.completed_date || ""))
    .slice(0, s.limit || 10);
  if (!list.length) return <BlockEmpty>No recent activity</BlockEmpty>;
  return (
    <div className="space-y-1">
      {list.map((a) => (
        <button key={a.id} onClick={() => openTask(a)} data-testid={`block-${block.id}-activity-${a.id}`} className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-slate-50">
          <TypeIcon type={a.activity_type || a.action_type} className="h-4 w-4 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-slate-800">{a.title}</p>
            <p className="truncate text-xs text-slate-400">{[a.organisation_name || a.property_name || a.contact_name, a.completed_by_name].filter(Boolean).join(" · ")}</p>
          </div>
          <span className="shrink-0 text-[11px] text-slate-400">{formatDateShort(a.completed_date)}</span>
        </button>
      ))}
    </div>
  );
}

export function AttentionBlock({ block }) {
  const { data } = useDash();
  const navigate = useNavigate();
  const list = data?.relationship_attention || [];
  if (!list.length) return <BlockEmpty>All relationships look healthy</BlockEmpty>;
  return (
    <div className="space-y-1">
      {list.map((o) => (
        <button key={o.id} onClick={() => navigate(`/organisations/${o.id}`)} data-testid={`block-${block.id}-org-${o.id}`} className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-slate-50">
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-slate-800">{o.name}</p>
            <p className="truncate text-xs text-slate-400">{o.reasons.join(" · ")}</p>
          </div>
          {o.relationship_health && <HealthPill health={o.relationship_health} />}
        </button>
      ))}
    </div>
  );
}
